import { useTranslation } from 'react-i18next';
import { AlertCircle, Calendar, Clock } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

interface RescheduleConfirmationModalProps {
  patientName: string;
  serviceName?: string;
  oldDate: Date;
  newDate: Date;
  oldTime: string;
  newTime: string;
  hasConflict?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RescheduleConfirmationModal({
  patientName,
  serviceName,
  oldDate,
  newDate,
  oldTime,
  newTime,
  hasConflict = false,
  onConfirm,
  onCancel,
}: RescheduleConfirmationModalProps) {
  const { t, i18n } = useTranslation();

  const dateFormatter = new Intl.DateTimeFormat(
    i18n.language === 'da' ? 'da-DK' : 'en-US',
    {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
  );

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const sameDay = isSameDay(oldDate, newDate);

  return (
    <Modal
      title={t('appointments.confirm_reschedule')}
      onClose={onCancel}
    >
      <div className="space-y-4">
        {/* Patient Info */}
        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
          <div className="font-semibold text-gray-900 dark:text-white">
            {patientName}
          </div>
          {serviceName && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {serviceName}
            </div>
          )}
        </div>

        {/* Change Details */}
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm">
            <Calendar className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <div className="flex-1">
              <div className="text-gray-500 dark:text-gray-400">
                {t('appointments.from')}:
              </div>
              <div className="font-medium text-gray-900 dark:text-white">
                {dateFormatter.format(oldDate)}
              </div>
            </div>
          </div>

          {!sameDay && (
            <div className="flex items-start gap-2 text-sm">
              <Calendar className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
              <div className="flex-1">
                <div className="text-gray-500 dark:text-gray-400">
                  {t('appointments.to')}:
                </div>
                <div className="font-medium text-indigo-600 dark:text-indigo-400">
                  {dateFormatter.format(newDate)}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 text-sm">
            <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <div className="flex-1">
              <div className="text-gray-500 dark:text-gray-400">
                {t('appointments.time_change')}:
              </div>
              <div className="font-medium text-gray-900 dark:text-white">
                {oldTime} → <span className="text-indigo-600 dark:text-indigo-400">{newTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Conflict Warning */}
        {hasConflict && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                {t('appointments.conflict_warning')}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="secondary"
            size="full"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="full"
            onClick={onConfirm}
          >
            {t('appointments.confirm_reschedule_button')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
